import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Award, Plus, Trash2, Eye, EyeOff, ExternalLink, CreditCard, Upload, Pencil, Lock, KeyRound, Trophy, PlaneTakeoff, BookOpen, AlarmClock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useStepUp } from "../StepUpGuard";
import { useTravelers } from "./useTravelers";
import PhotoAttach from "../PhotoAttach";

const UNITS = ["miles", "points"];
const empty = { airline_name: "", program_name: "", member_number: "", tier: "", balance: "", unit: "miles", tier_goal_name: "", tier_goal_target: "", expiry_date: "", notes: "", login_user: "", login_password: "", secret_phrase: "", login_link: "", traveler_name: "" };
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

const ExpiryBadge = ({ it }) => {
  const d = it.days_to_expiry;
  if (d == null) return null;
  const cls = d < 0 ? "bg-zinc-200 text-zinc-600" : d <= 30 ? "bg-rose-100 text-rose-700" : d <= 90 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700";
  const label = d < 0 ? `Expired ${it.expiry_date}` : d === 0 ? "Expires today!" : `Expires in ${d} day${d === 1 ? "" : "s"}`;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 rounded ${cls}`} data-testid={`loyalty-expiry-${it.id}`} title={`Miles expiry date: ${it.expiry_date}`}>
      <AlarmClock className="w-3 h-3" /> {label}
    </span>
  );
};

export default function LoyaltyManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [revealed, setRevealed] = useState({});
  const travelers = useTravelers();

  const load = () => api.get("/loyalty/records").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.airline_name.trim() && !form.program_name.trim()) { toast.error("Airline or program name is required"); return; }
    try {
      await api.post("/loyalty/records", form);
      setForm(empty); setOpen(false); load();
      toast.success(form.id ? "Loyalty record updated" : "Loyalty record added");
    } catch { toast.error("Could not save record"); }
  };

  const edit = (it) => { setForm({ ...it, member_number: "", login_user: "", login_password: "", secret_phrase: "", airline_name: it.airline_name ?? "", program_name: it.program_name ?? "", tier: it.tier ?? "", notes: it.notes ?? "", unit: it.unit || "miles", balance: it.balance ?? "", tier_goal_target: it.tier_goal_target ?? "", tier_goal_name: it.tier_goal_name ?? "", expiry_date: it.expiry_date ?? "", login_link: it.login_link ?? "", traveler_name: it.traveler_name ?? "" }); setOpen(true); };
  const remove = async (id) => { await api.delete(`/loyalty/records/${id}`); load(); toast.success("Removed"); };

  const guard = useStepUp();
  const toggleReveal = async (it) => {
    if (revealed[it.id]) { setRevealed((r) => ({ ...r, [it.id]: null })); return; }
    guard(async () => {
      try {
        const r = await api.get(`/loyalty/records/${it.id}/reveal`);
        setRevealed((s) => ({ ...s, [it.id]: r.data }));
      } catch { toast.error("Could not reveal — please try again"); }
    });
  };

  const logMiles = async (it, amount) => {
    const add = Number(amount);
    if (!add) return;
    const newBal = (it.balance_num || 0) + add;
    await api.post("/loyalty/records", { id: it.id, balance: newBal });
    toast.success(`Logged ${fmt(add)} ${it.unit} to ${it.program_name || it.airline_name}`);
    load();
  };

  return (
    <div data-testid="loyalty-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#F9AB00] text-2xl flex items-center gap-2"><Award className="w-6 h-6" /> My Loyalty & Mileage</h2>
        <button onClick={() => { setForm(empty); setOpen((o) => !o); }} data-testid="loyalty-new" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add program</button>
      </div>
      <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private to you. Member numbers, logins and secret phrases are masked by default — tap the eye to reveal.</p>

      <Link to="/loyalty#guide" data-testid="loyalty-guide-link" className="flex items-center gap-3 border border-amber-200 bg-amber-50/50 p-3 mb-6 hover:border-amber-400 transition-colors group">
        <BookOpen className="w-5 h-5 text-amber-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-600 text-sm text-amber-800">Guide: Why airline credit cards can be more valuable than you think</div>
          <div className="text-xs text-amber-800/70">Explore &amp; join all 33 programs and read the full miles guide</div>
        </div>
        <span className="text-amber-600 text-sm font-600 group-hover:translate-x-0.5 transition-transform">Read →</span>
      </Link>

      {items.length > 0 && <Leaderboard items={items} />}

      {(() => {
        const expiring = items.filter((i) => i.days_to_expiry != null && i.days_to_expiry >= 0 && i.days_to_expiry <= 90);
        return expiring.length > 0 && (
          <div className="border border-rose-300 bg-rose-50/60 p-3 mb-4" data-testid="loyalty-expiry-banner">
            <div className="text-sm font-700 text-rose-800 flex items-center gap-1.5 mb-1"><AlarmClock className="w-4 h-4" /> Miles expiring soon</div>
            <ul className="text-xs text-rose-800/90 space-y-0.5">
              {expiring.map((i) => (
                <li key={i.id} data-testid={`loyalty-expiry-warning-${i.id}`}>
                  <b>{i.program_name || i.airline_name}</b>: {i.balance_num != null ? `${fmt(i.balance_num)} ${i.unit}` : `your ${i.unit}`} expire on {i.expiry_date}
                  {" "}({i.days_to_expiry === 0 ? "today" : `in ${i.days_to_expiry} day${i.days_to_expiry === 1 ? "" : "s"}`}) — a single earn or redeem usually resets the clock.
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {open && (
        <div className="border border-amber-300 bg-white p-4 mb-4" data-testid="loyalty-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={form.airline_name} onChange={(e) => setForm({ ...form, airline_name: e.target.value })} placeholder="Airline (e.g. United Airlines)" data-testid="loyalty-airline" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.program_name} onChange={(e) => setForm({ ...form, program_name: e.target.value })} placeholder="Program (e.g. MileagePlus)" data-testid="loyalty-program" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.member_number} onChange={(e) => setForm({ ...form, member_number: e.target.value })} placeholder="Membership number" data-testid="loyalty-number" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <div className="flex gap-2">
              <input list="loyalty-traveler-options" value={form.traveler_name} onChange={(e) => setForm({ ...form, traveler_name: e.target.value })} placeholder="Whose program? (traveller)" data-testid="loyalty-traveler" className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
              <datalist id="loyalty-traveler-options">
                {travelers.map((t) => <option key={t.id} value={t.name} />)}
              </datalist>
            </div>
            <input value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} placeholder="Status / tier (e.g. Gold)" data-testid="loyalty-tier" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} placeholder="Current balance" data-testid="loyalty-balance" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="border border-zinc-300 px-3 py-2 text-sm bg-white">
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <input value={form.tier_goal_name} onChange={(e) => setForm({ ...form, tier_goal_name: e.target.value })} placeholder="Next tier goal name (e.g. Platinum)" data-testid="loyalty-goal-name" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.tier_goal_target} onChange={(e) => setForm({ ...form, tier_goal_target: e.target.value })} placeholder={`${form.unit} needed for next tier`} data-testid="loyalty-goal-target" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <label className="text-xs text-muted-foreground sm:col-span-2">Miles / points expiry date (optional — we'll warn you 90, 30 and 7 days before)
              <input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="loyalty-expiry-input" className="w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary mt-1" />
            </label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" className="sm:col-span-2 border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div className="mt-3 border-t border-amber-200 pt-3">
            <div className="text-xs font-700 text-amber-800 flex items-center gap-1.5 mb-2"><KeyRound className="w-3.5 h-3.5" /> Program login (optional — masked &amp; private to you{form.id ? "; leave blank to keep current" : ""})</div>
            <div className="grid sm:grid-cols-3 gap-2">
              <input value={form.login_user} onChange={(e) => setForm({ ...form, login_user: e.target.value })} placeholder="Login user ID" autoComplete="off" data-testid="loyalty-login-user" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
              <input value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} type="password" placeholder="Login password" autoComplete="new-password" data-testid="loyalty-login-password" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
              <input value={form.secret_phrase} onChange={(e) => setForm({ ...form, secret_phrase: e.target.value })} placeholder="Secret word / phrase (optional)" autoComplete="off" data-testid="loyalty-secret" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <input value={form.login_link} onChange={(e) => setForm({ ...form, login_link: e.target.value })} placeholder="Program login page link (optional, e.g. https://www.united.com/en/us/signin)" data-testid="loyalty-login-link" className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="loyalty-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">{form.id ? "Update" : "Save"} program</button>
          </div>
        </div>
      )}

      {items.length === 0 && !open ? (
        <p className="text-sm text-muted-foreground">No loyalty programs yet. Add your frequent-flyer memberships to track points, status and cards in one place.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <LoyaltyCard key={it.id} it={it} rev={revealed[it.id]} onReveal={() => toggleReveal(it)} onEdit={() => edit(it)} onRemove={() => remove(it.id)} onChange={load} onLogMiles={logMiles} />
          ))}
        </div>
      )}
    </div>
  );
}

function Leaderboard({ items }) {
  const totals = items.reduce((acc, it) => {
    const u = it.unit || "miles";
    acc[u] = (acc[u] || 0) + (it.balance_num || 0);
    return acc;
  }, {});
  const statuses = items.filter((i) => i.tier).length;
  const withGoal = items.filter((i) => i.tier_goal_num && i.balance_num != null);
  return (
    <div className="border border-amber-300 bg-white p-5 mb-6" data-testid="loyalty-leaderboard">
      <h3 className="font-heading font-900 text-amber-700 flex items-center gap-2 mb-4"><Trophy className="w-5 h-5" /> Your miles at a glance</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Object.entries(totals).map(([u, v]) => (
          <div key={u} data-testid={`loyalty-total-${u}`}>
            <div className="font-heading font-900 text-2xl tabular-nums">{fmt(v)}</div>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">total {u}</div>
          </div>
        ))}
        <div>
          <div className="font-heading font-900 text-2xl tabular-nums">{items.length}</div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">programs</div>
        </div>
        <div>
          <div className="font-heading font-900 text-2xl tabular-nums">{statuses}</div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">elite statuses</div>
        </div>
      </div>
      {withGoal.length > 0 && (
        <div className="mt-5 space-y-3">
          {withGoal.map((it) => {
            const pct = Math.min(100, Math.round((it.balance_num / it.tier_goal_num) * 100));
            return (
              <div key={it.id} data-testid={`loyalty-progress-${it.id}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-600">{it.program_name || it.airline_name} → {it.tier_goal_name || "Next tier"}</span>
                  <span className="text-muted-foreground tabular-nums">{fmt(it.balance_num)} / {fmt(it.tier_goal_num)} {it.unit} ({pct}%)</span>
                </div>
                <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LoyaltyCard({ it, rev, onReveal, onEdit, onRemove, onChange, onLogMiles }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [logging, setLogging] = useState(false);
  const [miles, setMiles] = useState("");
  const token = localStorage.getItem("token");

  const uploadCard = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/loyalty/records/${it.id}/card`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Card image uploaded (private)"); onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="border border-zinc-300 bg-white p-4 flex flex-col" data-testid={`loyalty-card-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-900 text-lg truncate">{it.program_name || it.airline_name}</div>
          <div className="text-xs text-muted-foreground truncate">{it.airline_name}{it.traveler_name ? ` · ${it.traveler_name}` : ""}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} data-testid={`loyalty-edit-${it.id}`} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
          <button onClick={onRemove} data-testid={`loyalty-remove-${it.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-sm">
        <ExpiryBadge it={it} />
        {it.tier && <div><span className="text-muted-foreground">Status: </span><span className="font-600">{it.tier}</span></div>}
        {it.balance != null && it.balance !== "" && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Balance: </span>
            <span className="font-600 tabular-nums">{fmt(it.balance_num)} {it.unit}</span>
            {it.balance_delta != null && it.balance_delta !== 0 && (
              <span className={`text-xs font-mono ${it.balance_delta > 0 ? "text-emerald-600" : "text-destructive"}`} data-testid={`loyalty-delta-${it.id}`}>
                {it.balance_delta > 0 ? "+" : ""}{fmt(it.balance_delta)}
              </span>
            )}
          </div>
        )}
        {it.has_number && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">No: </span>
            <span className="font-mono" data-testid={`loyalty-number-${it.id}`}>{rev?.member_number || it.member_number}</span>
            <button onClick={onReveal} data-testid={`loyalty-reveal-${it.id}`} className="text-primary hover:text-blue-700">
              {rev ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        {it.has_login && (
          <div className="border border-zinc-200 bg-zinc-50 p-2 space-y-1" data-testid={`loyalty-login-block-${it.id}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><KeyRound className="w-3 h-3" /> Program login
              <button onClick={onReveal} data-testid={`loyalty-login-reveal-${it.id}`} className="ml-auto text-primary hover:text-blue-700">
                {rev ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            {(rev?.login_user || it.login_user) && <div className="text-xs"><span className="text-muted-foreground">User: </span><span className="font-mono" data-testid={`loyalty-login-user-${it.id}`}>{rev?.login_user || it.login_user}</span></div>}
            {(rev?.login_password || it.login_password) && <div className="text-xs"><span className="text-muted-foreground">Password: </span><span className="font-mono" data-testid={`loyalty-login-pass-${it.id}`}>{rev?.login_password || it.login_password}</span></div>}
            {it.has_secret && <div className="text-xs"><span className="text-muted-foreground">Secret: </span><span className="font-mono" data-testid={`loyalty-login-secret-${it.id}`}>{rev?.secret_phrase || it.secret_phrase}</span></div>}
          </div>
        )}
        {it.notes && <div className="text-muted-foreground">{it.notes}</div>}
      </div>

      {it.has_card && (
        <a href={`${API}/loyalty/records/${it.id}/card?auth=${token}`} target="_blank" rel="noreferrer" className="mt-3 block" data-testid={`loyalty-card-img-${it.id}`}>
          <img src={`${API}/loyalty/records/${it.id}/card?auth=${token}`} alt="Loyalty card" className="w-full h-24 object-cover border border-zinc-200 rounded" />
        </a>
      )}

      <div className="mt-3">
        <PhotoAttach
          items={it.photos || []}
          testPrefix={`loyalty-photo-${it.id}`}
          label="Add a screenshot / image"
          onUpload={async (file) => {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/loyalty/records/${it.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
            onChange();
          }}
          onEdit={async (pid, patch) => { await api.put(`/loyalty/records/${it.id}/photos/${pid}`, patch); onChange(); }}
          onDelete={async (pid) => { await api.delete(`/loyalty/records/${it.id}/photos/${pid}`); onChange(); }}
        />
      </div>

      {logging && (
        <div className="mt-3 flex gap-2" data-testid={`loyalty-log-form-${it.id}`}>
          <input value={miles} onChange={(e) => setMiles(e.target.value)} type="number" placeholder={`Miles earned this flight`} className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <button onClick={() => { onLogMiles(it, miles); setMiles(""); setLogging(false); }} data-testid={`loyalty-log-save-${it.id}`} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-600">Add</button>
        </div>
      )}

      <div className="mt-auto pt-3 flex flex-wrap gap-2">
        <button onClick={() => setLogging((l) => !l)} data-testid={`loyalty-log-${it.id}`} className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-emerald-500 text-emerald-700">
          <PlaneTakeoff className="w-3.5 h-3.5" /> Log flight miles
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadCard} data-testid={`loyalty-card-file-${it.id}`} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-primary disabled:opacity-50">
          {busy ? <Upload className="w-3.5 h-3.5 animate-pulse" /> : <CreditCard className="w-3.5 h-3.5" />} {it.has_card ? "Replace card" : "Add card image"}
        </button>
        {it.official_link && (
          <a href={it.official_link} target="_blank" rel="noreferrer" data-testid={`loyalty-official-${it.id}`} className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-primary text-primary">
            <ExternalLink className="w-3.5 h-3.5" /> Official page
          </a>
        )}
        {it.login_link && (
          <a href={it.login_link} target="_blank" rel="noreferrer" data-testid={`loyalty-login-link-${it.id}`} className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-amber-500 text-amber-700">
            <KeyRound className="w-3.5 h-3.5" /> Login page
          </a>
        )}
      </div>
    </div>
  );
}
